/**
 * LoadingSpinner - 로딩 인디케이터 컴포넌트
 *
 * @param variant - 'circular' | 'dashed' | 'dots' | 'starburst' | 'grid' 애니메이션 종류
 * @param size - 'xs' ~ '2xl' 크기
 * @param showText - true이면 로딩 텍스트 함께 표시
 */
'use client';

import React from 'react';
import styles from './LoadingSpinner.module.css';

export interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  variant?: 'circular' | 'dashed' | 'dots' | 'starburst' | 'grid';
  color?: string;
  showText?: boolean;
  text?: string;
}

export default function LoadingSpinner({
  size = 'md',
  variant = 'circular',
  color,
  showText = false,
  text = 'Loading...',
}: LoadingSpinnerProps) {
  const wrapperClass = showText ? styles.withText : '';

  // Dots variant
  if (variant === 'dots') {
    return (
      <div className={`${styles.container} ${wrapperClass}`}>
        <div
          className={`${styles.dots} ${styles[size]}`}
          role="status"
          aria-label="Loading"
          style={color ? { color } : undefined}
        >
          <span className={styles.dot} />
          <span className={styles.dot} />
          <span className={styles.dot} />
          <span className={styles.srOnly}>Loading...</span>
        </div>
        {showText && <span className={styles.loadingText}>{text}</span>}
      </div>
    );
  }

  // Starburst variant: radiating lines
  if (variant === 'starburst') {
    return (
      <div className={`${styles.container} ${wrapperClass}`}>
        <div
          className={`${styles.starburst} ${styles[size]}`}
          role="status"
          aria-label="Loading"
          style={color ? { color } : undefined}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <span
              key={i}
              className={styles.starburstLine}
              style={{ transform: `rotate(${i * 45}deg)`, animationDelay: `${i * 0.1}s` }}
            />
          ))}
          <span className={styles.srOnly}>Loading...</span>
        </div>
        {showText && <span className={styles.loadingText}>{text}</span>}
      </div>
    );
  }

  // Grid variant: 3x3 pulsing dots
  if (variant === 'grid') {
    return (
      <div className={`${styles.container} ${wrapperClass}`}>
        <div
          className={`${styles.grid} ${styles[size]}`}
          role="status"
          aria-label="Loading"
          style={color ? { color } : undefined}
        >
          {Array.from({ length: 4 }).map((_, i) => (
            <span key={i} className={styles.gridCell} style={{ animationDelay: `${i * 0.15}s` }} />
          ))}
          <span className={styles.srOnly}>Loading...</span>
        </div>
        {showText && <span className={styles.loadingText}>{text}</span>}
      </div>
    );
  }

  // Circular / dashed variants
  const spinnerCls = [styles.spinner, styles[size], variant === 'dashed' && styles.dashed]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`${styles.container} ${wrapperClass}`}>
      <div
        className={spinnerCls}
        role="status"
        aria-label="Loading"
        style={color ? { borderTopColor: color } : undefined}
      >
        <span className={styles.srOnly}>Loading...</span>
      </div>
      {showText && <span className={styles.loadingText}>{text}</span>}
    </div>
  );
}
